import useCustomToast from '@/components/common/Toast';
import { useChangePassword } from '@/services/account-setting';
import { IChangePassword } from '@/services/account-setting/types';
import { Button, useDisclosure, InputGroup } from '@chakra-ui/react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  Card,
  Link,
} from '@chakra-ui/react';
import { Box, Text, Stack, HStack, Icon } from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm, Controller } from 'react-hook-form';
import * as yup from 'yup';
import { useState } from 'react';
import CustomInput from '@/components/common/Input';
import CheckIcon from '@/assets/svg/check-mark-validation.svg?react';
import { AxiosError } from 'axios';

const validationSchema = yup.object().shape({
  old_password: yup.string().required('Current password is required'),
  new_password: yup
    .string()
    .required('New password is required')
    .notOneOf(
      [yup.ref('old_password'), null],
      'New password must be different from the current password',
    )
    .min(8, 'Must be at least 8 characters')
    .matches(/^(?=.*[!@#$%^&*])/, 'Must contain one special character'),
  confirm_password: yup
    .string()
    .oneOf([yup.ref('new_password'), undefined], 'Passwords must match')
    .required('Confirm password is required'),
});

const ChangePassword = () => {
  const { showToast } = useCustomToast();
  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    setError,
    watch,
  } = useForm<IChangePassword>({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      old_password: '',
      new_password: '',
      confirm_password: '',
    },
  });

  const {
    isOpen: isChangePasswordOpen,
    onOpen: onChangePasswordOpen,
    onClose: onChangePasswordClose,
  } = useDisclosure();

  const [showOldPassword] = useState(false);
  const [showNewPassword] = useState(false);
  const [showConfirmPassword] = useState(false);

  const handleChangePassword = () => {
    onChangePasswordOpen();
  };

  const handleCancel = () => {
    reset();
  };

  const { mutateAsync: changePassword } = useChangePassword();

  const onSubmit = (data: IChangePassword) => {
    // handle password change logic here
    changePassword(data)
      .then((data) => {
        showToast('success', data?.message ?? 'Password changed successfully');
        onChangePasswordClose();
        reset();
      })
      .catch((error: unknown) => {
        if (error instanceof AxiosError) {
          // handling http errors
          // normal error have a message key while validation error should have a form field name that is passed in post request as a key
          if (error.response) {
            if (error.response?.data?.old_password) {
              setError('old_password', {
                type: 'manual',
                message: error.response?.data?.old_password[0],
              });
            } else if (error.response?.data?.new_password) {
              setError('new_password', {
                type: 'manual',
                message: error.response?.data?.new_password[0],
              });
            } else if (error.response?.data?.confirm_password) {
              setError('confirm_password', {
                type: 'manual',
                message: error.response?.data?.confirm_password[0],
              });
            } else {
              showToast('error', error?.response?.data?.error ?? 'Something went wrong');
            }
          } else {
            showToast('error', 'Network Error');
          }
        }
      });
  };

  // min length and special character check
  const password = watch('new_password');
  const isMinLengthValid = password?.length >= 8;
  const hasSpecialChar = /[\W_]/.test(password);

  return (
    <>
      <Card
        p={4}
        shadow={'none'}
        borderColor={'border.default60'}
        borderWidth="1px"
        borderRadius="lg"
        bg={'background.white'}
      >
        <Link color={'brand.primary.DEFAULT'} onClick={handleChangePassword}>
          Change Password{' '}
        </Link>
      </Card>
      <Modal isOpen={isChangePasswordOpen} onClose={onChangePasswordClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Change Password</ModalHeader>
          <ModalCloseButton />
          <form onSubmit={handleSubmit(onSubmit)}>
            <ModalBody>
              <FormControl isInvalid={!!errors.old_password}>
                <InputGroup>
                  <Controller
                    name="old_password"
                    control={control}
                    render={({ field }) => (
                      <CustomInput
                        error={errors.old_password}
                        label="Current Password"
                        type={showOldPassword ? 'text' : 'password'}
                        {...field}
                      />
                    )}
                  />
                </InputGroup>
              </FormControl>
              <FormControl mt={4} isInvalid={!!errors.new_password}>
                <InputGroup>
                  <Controller
                    name="new_password"
                    control={control}
                    render={({ field }) => (
                      <CustomInput
                        error={errors.new_password}
                        label="New Password"
                        type={showNewPassword ? 'text' : 'password'}
                        {...field}
                      />
                    )}
                  />
                </InputGroup>
              </FormControl>
              <FormControl mt={4} isInvalid={!!errors.confirm_password}>
                <InputGroup>
                  <Controller
                    name="confirm_password"
                    control={control}
                    render={({ field }) => (
                      <CustomInput
                        error={errors.confirm_password}
                        label="Confirm New Password Password"
                        type={showConfirmPassword ? 'text' : 'password'}
                        {...field}
                      />
                    )}
                  />
                </InputGroup>
              </FormControl>
              {/* Password Guidelines */}
              <Stack pt={1} spacing={1}>
                <HStack>
                  <Box
                    bg={isMinLengthValid ? 'status.success' : 'border.default'}
                    borderRadius="full"
                    p={2}
                    height={'14px'}
                    width={'14px'}
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                  >
                    <Icon as={CheckIcon} color="red" fontSize={'8px'} />
                  </Box>
                  <Text fontSize="xs" color={'text.secondary'} fontFamily={'primary'}>
                    Must be at least 8 characters
                  </Text>
                </HStack>

                <HStack>
                  <Box
                    bg={hasSpecialChar ? 'status.success' : 'border.default'}
                    borderRadius="full"
                    p={2}
                    height={'14px'}
                    width={'14px'}
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                  >
                    <Icon as={CheckIcon} color="white" fontSize={'8px'} />
                  </Box>
                  <Text fontSize="xs" color={'text.secondary'} fontFamily={'primary'}>
                    Must contain one special character
                  </Text>
                </HStack>
              </Stack>
            </ModalBody>

            <ModalFooter justifyContent={'space-between'}>
              <Button colorScheme="brand.primary" variant={'outline'} mr={3} onClick={handleCancel}>
                Cancel
              </Button>
              <Button colorScheme="brand.primary" type="submit">
                Submit
              </Button>
            </ModalFooter>
          </form>
        </ModalContent>
      </Modal>
    </>
  );
};

export default ChangePassword;
